import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { HistoryService } from './history.service';
import { CloseHistoryStats, GetHistoryStats, OpenHistoryStats } from './history.actions';
import { patch } from '@ngxs/store/operators';
import { HistoryItemStats } from './models/history-item-stats.model';
import { tap } from 'rxjs';
import { LoadingModel } from '../../../_core/state/loading.model';
import { finalize } from 'rxjs/operators';
import { HistoryItem } from '../../../models/history-user';

export type HistoryStateModel = LoadingModel & {
  isStatsModalOpen: boolean;
  historyItemStats?: HistoryItemStats;
  selectedHistoryItem?: HistoryItem;
}

export const initialState: HistoryStateModel = {
  isLoading: false,
  isStatsModalOpen: false,
  historyItemStats: undefined,
  selectedHistoryItem: undefined,
}

@State<HistoryStateModel>({
  name: 'history',
  defaults: initialState,
})
@Injectable()
export class HistoryState {
  private readonly historyService = inject(HistoryService);

  @Selector()
  static isStatsModalOpen(state: HistoryStateModel): boolean {
    return state.isStatsModalOpen;
  }

  @Selector()
  static historyItemStats(state: HistoryStateModel): HistoryItemStats | undefined {
    return state.historyItemStats;
  }

  @Selector()
  static selectedHistoryItem(state: HistoryStateModel): HistoryItem | undefined {
    return state.selectedHistoryItem;
  }

  @Selector()
  static isLoading(state: HistoryStateModel) {
    return state.isLoading;
  }

  @Action(GetHistoryStats)
  getHistoryStats(context: StateContext<HistoryStateModel>, { recordId, type, parentId }: GetHistoryStats) {
    context.setState(patch({ isLoading: true }));

    return this.historyService.getHistoryItemStats(recordId, type, parentId).pipe(
      tap((item) => {
        context.setState(patch({ historyItemStats: item }));
      }),
      finalize(() =>
        context.setState(patch({ isLoading: false }))
      ),
    )
  }

  @Action(OpenHistoryStats)
  openHistoryStats(context: StateContext<HistoryStateModel>, { historyItem, type, parentId }: OpenHistoryStats) {
    context.setState(patch({ isStatsModalOpen: true, selectedHistoryItem: historyItem }));

    context.dispatch(new GetHistoryStats(historyItem.recordId, type, parentId));
  }

  @Action(CloseHistoryStats)
  closeHistoryStats(context: StateContext<HistoryStateModel>) {
    context.setState(patch({ isStatsModalOpen: false, historyItemStats: undefined, selectedHistoryItem: undefined }));
  }
}

